import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { useDestroyProjectPersonnelMutation } from 'components/hooks/projectPersonnelMutations';
import { useCreateReviewReminderMutation } from 'components/hooks/reviewReminderMutations';
import { useCreateReviewResetMutation } from 'components/hooks/reviewResetMutations';

import ConditionalWrapper from 'components/application/ConditionalWrapper';
import DestroyModal from 'components/application/DestroyModal';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import ProjectPersonnelRemovedToast from 'components/projectPersonnel/components/ProjectPersonnelRemovedToast';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

import { personDisplayName } from 'components/helpers/users';

export default function ProjectPersonnelRowOptions({
  onDestroySuccess,
  personnel,
  projectPersonnelId,
  review,
}) {
  const { addInedibleToast, addToast } = useBreadBoard();
  const currentActor = useCurrentActor();

  const { projectId, projectPersonnelPolicy, reviewPolicy } =
    useProjectPersonnelContext();

  const createReviewReminderMutation = useCreateReviewReminderMutation();

  const createReviewResetMutation = useCreateReviewResetMutation({ projectId });

  const destroyProjectPersonnelMutation = useDestroyProjectPersonnelMutation({
    projectId,
  });

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectPersonnel.components.ProjectPersonnelRowOptions',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleDestroy = () => {
    destroyProjectPersonnelMutation.mutate(
      { id: projectPersonnelId },
      {
        onSuccess: () => {
          addToast(<ProjectPersonnelRemovedToast displayName={displayName} />);
          onDestroySuccess();
        },
        onError: () => {
          addInedibleToast({
            fullMessage: (
              <Trans
                i18nKey='projectPersonnel.shared.destroyProjectPersonnel.error'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ displayName, singularRAMSName }}
              />
            ),
          });
        },
      },
    );
  };

  const handleReset = () => {
    createReviewResetMutation.mutate(
      { id: review.id },
      {
        onError: () => {
          addInedibleToast({
            fullMessage: (
              <Trans
                i18nKey='projectPersonnel.components.ProjectPersonnelRowOptions.handleReset.error'
                ns='components'
              />
            ),
          });
        },
      },
    );
  };

  const handleSendReminder = () => {
    createReviewReminderMutation.mutate(
      { id: review.id },
      {
        onError: () => {
          addInedibleToast({
            fullMessage: (
              <Trans
                i18nKey='projectPersonnel.components.ProjectPersonnelRowOptions.handleSendReminder.error'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ displayName }}
              />
            ),
          });
        },
      },
    );
  };

  const isAccepted = review?.attributes.state === 'accepted';
  const isRejected = review?.attributes.state === 'rejected';

  const isSendReminderOptionDisabled =
    isAccepted || isRejected || !personnel?.attributes.email;

  const isSignNowOptionDisabled = isAccepted || isRejected;

  const displayName = personDisplayName({ attributes: personnel.attributes });

  const { singularRAMSName } = currentActor.division.attributes;

  return (
    <Options
      additional={
        <DestroyModal
          confirmationText={i18nT('destroyModal.confirmationText', {
            interpolation: { escapeValue: false },
            displayName,
          })}
          displayText={i18nTCommon('shared.unassignNameConfirmation', {
            interpolation: { escapeValue: false },
            name: displayName,
          })}
          isOpen={isDestroyModalOpen}
          onClose={() => setIsDestroyModalOpen(false)}
          onDestroy={handleDestroy}
        />
      }
      key={projectPersonnelId}
    >
      {reviewPolicy.canCreate &&
        currentActor.isAllowedFeature('digital_signatures') &&
        review && (
          <OptionChoice disabled={isSignNowOptionDisabled}>
            <ConditionalWrapper
              condition={isSignNowOptionDisabled}
              wrapper={(children) => (
                <Tooltip
                  placement='top'
                  tooltip={
                    isAccepted ?
                      i18nTCommon('personnel.signedDocument')
                    : i18nTCommon('personnel.rejectedDocument')
                  }
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <a
                className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'
                href={
                  isSignNowOptionDisabled ? '#' : (
                    `/reviews/${review.attributes.accessToken}?in_person=true`
                  )
                }
              >
                {i18nT('options.signNow.button')}
              </a>
            </ConditionalWrapper>
          </OptionChoice>
        )}
      {reviewPolicy.canRemind && review && personnel && (
        <OptionChoice
          disabled={isSendReminderOptionDisabled}
          onClick={(setOptionsOpen) => {
            setOptionsOpen(false);
            handleSendReminder();
          }}
        >
          <ConditionalWrapper
            condition={isSendReminderOptionDisabled}
            wrapper={(children) => (
              <Tooltip
                placement='top'
                tooltip={
                  isAccepted ? i18nTCommon('personnel.signedDocument')
                  : isRejected ?
                    i18nTCommon('personnel.rejectedDocument')
                  : i18nTCommon('personnel.noEmailAddressForPersonnel')
                }
                trigger='hover'
              >
                {children}
              </Tooltip>
            )}
          >
            <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
              {i18nT('options.sendReminder.button')}
            </a>
          </ConditionalWrapper>
        </OptionChoice>
      )}
      {projectPersonnelPolicy.canDestroy && (
        <OptionChoice>
          <a
            className='tw-text-m tw-font-medium tw-tracking-auto tw-text-red-600 hover:tw-text-red-600'
            onClick={() => setIsDestroyModalOpen(true)}
          >
            {i18nTCommon('projects.unassignFromProject', {
              interpolation: { escapeValue: false },
              singularRAMSName,
            })}
          </a>
        </OptionChoice>
      )}
      {reviewPolicy.canReset && isRejected && (
        <OptionChoice
          onClick={(setOptionsOpen) => {
            setOptionsOpen(false);
            handleReset();
          }}
        >
          <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nT('options.reset')}
          </a>
        </OptionChoice>
      )}
    </Options>
  );
}

ProjectPersonnelRowOptions.propTypes = {
  onDestroySuccess: PropTypes.func.isRequired,
  personnel: PropTypes.object.isRequired,
  projectPersonnelId: PropTypes.string.isRequired,
  review: PropTypes.object,
};
